// Client-side CSV/Excel exporter over already-fetched `RigFracPointRow[]`.
// The Data Table's `useExportMutation`/`ExportButton` are server-side and
// entityKind-coupled, so they're not reusable here.
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

import { RigFracPointRow } from "api/rigfrac";

import { RIGFRAC_COLUMNS, RigFracColumnKey, getColumnDef } from "./types";

// Leading chars that a spreadsheet application (Excel/Sheets) treats as the
// start of a formula on CSV import (OWASP A05 — CSV/formula injection). A
// cell like `=HYPERLINK(...)` in a free-text column (operator/basin/etc.)
// would otherwise execute as a formula when the file is opened.
const FORMULA_LEADING_CHARS = /^[=+\-@\t\r]/;

// A plain (optionally negative/decimal) number, e.g. `-104.1234` — never
// neutralized even though it starts with `-`, since it isn't a formula.
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

function escapeCsvField(value: string, neutralizeFormula = true): string {
  const field =
    neutralizeFormula && !PLAIN_NUMBER.test(value) && FORMULA_LEADING_CHARS.test(value)
      ? `'${value}`
      : value;
  if (field.includes(",") || field.includes('"') || field.includes("\n")) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

export interface RigFracExportTable {
  header: string[];
  body: string[][];
}

export function buildRigFracExportTable(
  rows: RigFracPointRow[],
  columns: RigFracColumnKey[]
): RigFracExportTable {
  const defs = columns.map((key) => getColumnDef(key));
  const header = defs.map((d) => d.name);
  const body = rows.map((row) => defs.map((d) => d.format(row)));
  return { header, body };
}

export function exportRigFracRowsToCsv(
  rows: RigFracPointRow[],
  columns: RigFracColumnKey[],
  fileName = "rig-frac-data.csv"
): void {
  const { header, body } = buildRigFracExportTable(rows, columns);

  const lines = [header, ...body].map((line) =>
    line.map((value) => escapeCsvField(value)).join(",")
  );

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, fileName);
}

export function exportRigFracRowsToExcel(
  rows: RigFracPointRow[],
  columns: RigFracColumnKey[],
  fileName = "rig-frac-data.xlsx"
): void {
  const { header, body } = buildRigFracExportTable(rows, columns);

  const worksheetData = [header, ...body];
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Rig & Frac Data");
  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  saveAs(blob, fileName);
}

export function exportRigFracRows(
  rows: RigFracPointRow[],
  columns: RigFracColumnKey[],
  format: "CSV" | "Excel",
  fileName?: string
): void {
  if (format === "CSV") {
    exportRigFracRowsToCsv(rows, columns, fileName);
  } else {
    exportRigFracRowsToExcel(rows, columns, fileName);
  }
}

// Re-exported so column-catalogue consumers of this file don't need a
// separate import from ./types for the common case.
export { RIGFRAC_COLUMNS };
